/**
 * ElderGus — the second Cat Elder (Babylon.js port).
 *
 * A distinguished tabby who teaches "The Wisdom of the Still Point"
 * — finding calm at the center of chaos — and unlocks the Chronos Bubble.
 *
 * Appears after the Apex puzzle in the Midnight Burrow.
 * Primarily visual — interaction is via the CutsceneSystem.
 *
 * Visual: cool blue/purple-tinted capsule with a bioluminescent point-light
 * glow that orbits gently, plus a subtle idle bob.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { COLORS } from "../../config/GameConfig";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";

const ELDER_GUS_MODEL = "/assets/models/npcs/elder_gus.glb";

export class ElderGus {
  public mesh: Mesh;
  private material: StandardMaterial;
  private glowLight: PointLight;
  private baseY: number;
  private visualModel: InstantiatedModel | null = null;

  constructor(scene: Scene, position: Vector3) {
    this.baseY = position.y;

    // Body — cool blue/grey capsule (fallback until GLB loads)
    this.mesh = MeshBuilder.CreateCapsule(
      "elder_gus",
      { height: 1.5, radius: 0.38, tessellation: 16, subdivisions: 6 },
      scene,
    );
    this.mesh.position = position.clone();

    this.material = new StandardMaterial("elder_gus_mat", scene);
    this.material.diffuseColor = Color3.FromHexString('#6666aa');
    this.material.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE).scale(0.12);
    this.material.specularColor = Color3.Black();
    this.mesh.material = this.material;

    // Cool, contemplative glow — blue/purple point light
    this.glowLight = new PointLight("elder_gus_glow", Vector3.Zero(), scene);
    this.glowLight.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE);
    this.glowLight.specular = Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE);
    this.glowLight.intensity = 2.5;
    this.glowLight.range = 7;
    this.glowLight.parent = this.mesh;
    this.glowLight.position = new Vector3(0, 0.5, 0);

    getAssetManager()
      .instantiateModel(scene, ELDER_GUS_MODEL)
      .then((model) => {
        if (this.mesh.isDisposed()) {
          model.dispose();
          return;
        }
        this.visualModel = model;
        for (const root of model.rootNodes) {
          if (root instanceof TransformNode) {
            root.parent = this.mesh;
            root.position.copyFromFloats(0, -0.75, 0);
            root.scaling.setAll(2.2);
          }
        }
        for (const m of model.meshes) m.isPickable = false;
        this.mesh.isVisible = false;
      })
      .catch((err) => {
        console.warn("[ElderGus] Failed to load model", ELDER_GUS_MODEL, err);
      });
  }

  /** Gentle idle bob animation — call each frame. */
  update(time: number, _deltaSeconds: number): void {
    this.mesh.position.y = this.baseY + Math.sin(time * 0.8) * 0.03;

    // Orbit the glow light gently around the mesh
    const orbitRadius = 0.7;
    this.glowLight.position.x = Math.sin(time * 0.4) * orbitRadius;
    this.glowLight.position.z = Math.cos(time * 0.4) * orbitRadius;

    // Subtle intensity pulse
    this.glowLight.intensity = 2.0 + Math.sin(time * 1.2) * 0.4;
  }

  dispose(): void {
    this.visualModel?.dispose();
    this.visualModel = null;
    this.glowLight.dispose();
    this.material.dispose();
    this.mesh.dispose();
  }
}
